import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { BremaLogo } from "@/components/BremaLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import ErrorBoundary from "@/components/ErrorBoundary";
import {
  LayoutDashboard, Radar, Users, Store, LogOut, Menu, X, Newspaper, PackageOpen, ClipboardList, Inbox, Bot, BarChart3, Megaphone, Trophy, Settings as SettingsIcon, BookUser,
} from "lucide-react";

const ADMIN_NAV = [
  { to: "/", label: "Centro di Comando", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/agent-modules", label: "Moduli AI", icon: Bot, testid: "nav-agent-modules" },
  { to: "/sales-agent", label: "AI Sales Agent", icon: Radar, testid: "nav-sales" },
  { to: "/ambassadors", label: "Ambassador Finder", icon: Users, testid: "nav-ambassadors" },
  { to: "/media-finder", label: "Media & PR Finder", icon: Newspaper, testid: "nav-media" },
  { to: "/inbox", label: "Messaggi", icon: Inbox, testid: "nav-inbox" },
  { to: "/contacts", label: "Contatti / Rubrica", icon: BookUser, testid: "nav-contacts" },
  { to: "/catalog-admin", label: "Catalogo & Materiali", icon: PackageOpen, testid: "nav-catalog-admin" },
  { to: "/orders-admin", label: "Ordini & Rivenditori", icon: ClipboardList, testid: "nav-orders-admin" },
  { to: "/sales-stats", label: "Statistiche di Vendita", icon: BarChart3, testid: "nav-sales-stats" },
  { to: "/press-lounge-admin", label: "Gestione Press Lounge", icon: Megaphone, testid: "nav-press-lounge", badgeKey: "press" },
  { to: "/ambassador-lounge-admin", label: "Gestione Ambassador Lounge", icon: Trophy, testid: "nav-ambassador-lounge", badgeKey: "ambassador" },
  { to: "/settings", label: "Impostazioni", icon: SettingsIcon, testid: "nav-settings" },
];

const RESELLER_NAV = [
  { to: "/", labelKey: "nav.dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/portal", labelKey: "nav.portal", icon: Store, testid: "nav-portal" },
];

function NavItems({ items, onNavigate, unread = 0, counts = {} }) {
  return (
    <nav className="flex flex-col gap-1">
      {items.map((it) => {
        const Icon = it.icon;
        let badge = it.to === "/inbox" && unread > 0 ? unread : null;
        if (it.badgeKey && counts[it.badgeKey] > 0) badge = counts[it.badgeKey];
        return (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            data-testid={it.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-brema-hover hover:text-foreground"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px]" />
            <span className="flex-1">{it.label}</span>
            {badge && (
              <span data-testid={it.badgeKey ? `nav-badge-${it.badgeKey}` : "nav-inbox-badge"}
                className="ml-auto min-w-[20px] rounded-full bg-red-500 px-1.5 py-0.5 text-center text-[10px] font-bold text-white">
                {badge}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [loungeCounts, setLoungeCounts] = useState({ press: 0, ambassador: 0 });
  const isAdmin = user?.role === "admin";
  const items = isAdmin ? ADMIN_NAV : RESELLER_NAV.map((it) => ({ ...it, label: t(it.labelKey) }));

  useEffect(() => {
    if (user?.role !== "admin") return;
    const fetchUnread = () => {
      api.get("/inbound/unread-count").then(({ data }) => setUnread(data.count || 0)).catch(() => {});
      api.get("/lounge/pending-counts").then(({ data }) => setLoungeCounts({ press: data.press || 0, ambassador: data.ambassador || 0 })).catch(() => {});
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 60000);
    return () => clearInterval(t);
  }, [user?.role, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border px-5 py-5">
        <BremaLogo />
        <button
          className="lg:hidden text-muted-foreground"
          onClick={() => setMobileOpen(false)}
          data-testid="close-sidebar-btn"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-5">
        <p className="px-3.5 pb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          {isAdmin ? "Brand" : t("nav.reseller")}
        </p>
        <NavItems items={items} onNavigate={() => setMobileOpen(false)} unread={unread} counts={loungeCounts} />
      </div>
      <div className="border-t border-border p-3">
        <div className="mb-2 rounded-lg bg-brema-bg px-3.5 py-2.5">
          <p className="truncate text-sm font-medium text-foreground" data-testid="current-user-name">
            {user?.shop_name || user?.name}
          </p>
          <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="flex w-full items-center gap-2.5 rounded-lg px-3.5 py-2.5 text-sm font-medium text-foreground transition-colors duration-200 hover:bg-brema-hover hover:text-foreground"
        >
          <LogOut className="h-[18px] w-[18px]" />
          {isAdmin ? "Esci" : t("nav.logout")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-border bg-brema-bg lg:block">
        {SidebarInner}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-72 bg-brema-bg shadow-2xl animate-fade-up">
            {SidebarInner}
          </div>
        </div>
      )}

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-background/95 px-4 py-3 backdrop-blur sm:px-6 lg:px-8">
          <button
            onClick={() => setMobileOpen(true)}
            data-testid="open-sidebar-btn"
            className="text-foreground lg:hidden"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="lg:hidden"><BremaLogo compact /></div>
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
          </div>
        </header>
        <main className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
